import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, DatePicker, InputNumber, Select, message, Upload, Switch, Row, Col, Space, Button, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { CategoryService } from '@/services/category.service';
import { ClubService } from '@/services/club.service';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { Tournament } from '@/types/api.types';
import type { Category } from '@/types/models/tournament';
import type { Club } from '@/types/api.types';
import { TOURNAMENT_STATUS, TOURNAMENT_STATUS_LABELS } from '@/constants/tournament.constants';
import { logger } from '@/utils/logger';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { customLocale, getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';

// Настраиваем dayjs для русской локализации
dayjs.locale('ru');

interface EditTournamentModalProps {
  visible: boolean;
  tournament: Tournament | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export const EditTournamentModal: React.FC<EditTournamentModalProps> = ({
  visible,
  tournament,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [documentFileList, setDocumentFileList] = useState<UploadFile[]>([]);
  const [coverImageFileList, setCoverImageFileList] = useState<UploadFile[]>([]);
  const [tournamentData, setTournamentData] = useState<Tournament | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  // Группируем категории по полу и фильтруем по поиску
  const categoriesByGender = useMemo(() => {
    let filtered = categories;
    
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = categories.filter(c => 
        c.name.toLowerCase().includes(searchLower) ||
        `${c.ageFrom}-${c.ageTo}`.includes(searchLower) ||
        `${c.weightFrom}-${c.weightTo}`.includes(searchLower) ||
        (c.gender === 'male' && searchLower.includes('муж')) ||
        (c.gender === 'female' && searchLower.includes('жен'))
      );
    }
    
    const male = filtered.filter(c => c.gender === 'male');
    const female = filtered.filter(c => c.gender === 'female');
    return { male, female };
  }, [categories, searchText]);

  // Отслеживаем готовность формы
  useEffect(() => {
    if (form) {
      setFormReady(true);
    }
  }, [form]);

  // Загружаем актуальные данные турнира при открытии модального окна
  useEffect(() => {
    const loadTournamentData = async () => {
      if (visible && tournament?.id) {
        try {
          const freshTournamentData = await TournamentService.getById(tournament.id);
          setTournamentData(freshTournamentData);
                    
        } catch (error) {
          logger.error('Error loading tournament data:', error);
          // Если не удалось загрузить, используем переданные данные
          setTournamentData(tournament);
        }
      } else {
        setTournamentData(tournament);
      }
    };

    loadTournamentData();
  }, [visible, tournament?.id]);

  const fetchCategories = async () => {
    try {
      logger.info('Starting to fetch categories');
      setLoadingCategories(true);

      // Используем CategoryService для загрузки категорий
      const fetchedCategories = await CategoryService.getAll();    

      setCategories(fetchedCategories);
    } catch (error) {
      logger.error('Failed to fetch categories:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      message.error('Не удалось загрузить категории');
    } finally {      
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchCategories();
      fetchClubs();
    }
  }, [visible]);

  const fetchClubs = async () => {
    try {
      setLoadingClubs(true);
      const response = await ClubService.getClubs({ limit: 1000 });
      const clubsList = Array.isArray(response.data) ? response.data : response.data.rows || [];
      setClubs(clubsList);
    } catch (error) {
      logger.error('Error loading clubs:', error);
      message.warning('Не удалось загрузить список клубов');
    } finally {
      setLoadingClubs(false);
    }
  };

  // Инициализация формы при открытии модального окна
  useEffect(() => {
    if (!visible) {
      // Если модальное окно закрыто, сбрасываем список файлов и трансляций
      setDocumentFileList([]);
      setCoverImageFileList([]);
      setTournamentData(null);
      return;
    }

    if (visible && tournamentData && form && formReady) {
      // Проверяем наличие tournamentCategories и извлекаем ID категорий
      const categoryIds = tournamentData.tournamentCategories?.length
        ? tournamentData.tournamentCategories.map(tc => tc.weightCategory?.id).filter(Boolean)
        : [];      

      // Устанавливаем изображение обложки, если оно есть
      if (tournamentData.coverImage) {
        setCoverImageFileList([{
          uid: '-1',
          name: 'cover.jpg',
          status: 'done',
          url: tournamentData.coverImage,
        }]);
      }

      // Сбрасываем форму перед установкой новых значений
      form.resetFields();
      
      // Используем setTimeout для обеспечения того, что форма полностью инициализирована
      setTimeout(() => {
        try {
          if (form && visible) {
            const invitedClubIds = tournamentData.invitedClubIds || [];
            
            // Обрабатываем boolean значения - преобразуем строку в boolean
            const autoRegistration = typeof tournamentData.autoRegistration === 'string'
              ? tournamentData.autoRegistration === 'true'
              : tournamentData.autoRegistration;
            const isInviteOnly = typeof tournamentData.isInviteOnly === 'string'
              ? tournamentData.isInviteOnly === 'true'
              : tournamentData.isInviteOnly;
            const hasTwoThirdPlaces = typeof tournamentData.hasTwoThirdPlaces === 'string'
              ? tournamentData.hasTwoThirdPlaces === 'true'
              : tournamentData.hasTwoThirdPlaces;                        
            
            const formValues = {
              name: tournamentData.name,
              description: tournamentData.description,
              location: tournamentData.location,
              tournamentDate: dayjs(tournamentData.tournamentDate),
              registrationDeadline: dayjs(tournamentData.registrationDeadline),
              weighingDates: tournamentData.weighingDates?.map(date => dayjs(date)),
              feeForClubMembers: tournamentData.feeForClubMembers,
              feeForOthers: tournamentData.feeForOthers,
              categoryIds: categoryIds,
              status: tournamentData.status,
              autoRegistration: autoRegistration,
              isInviteOnly: isInviteOnly,
              hasTwoThirdPlaces: hasTwoThirdPlaces,
              disciplineType: tournamentData.disciplineType ?? undefined,
              victoryByPoints: tournamentData.victoryByPoints ?? undefined,
              invitedClubIds: invitedClubIds,
            };
            
            form.setFieldsValue(formValues);
            // Синхронизируем selectedCategories с загруженными categoryIds
            setSelectedCategories(categoryIds as number[]);            
            
          }
        } catch (error) {
          logger.error('Error initializing form:', error);
        }
      }, 0);
    }
  }, [visible, tournamentData, form, formReady]);

  const handleSubmit = async () => {
    if (!tournamentData || !form) {
      logger.error('Tournament or form is not available');
      return;
    }

    const tournamentId = tournamentData.id;

    try {
      setSubmitting(true);
      const values = await form.validateFields();

      // Создаем FormData для отправки данных и файлов
      const formData = new FormData();

      // Добавляем все поля турнира в FormData
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('location', values.location);
      formData.append('tournamentDate', values.tournamentDate.format('YYYY-MM-DD'));
      formData.append('registrationDeadline', values.registrationDeadline.format('YYYY-MM-DD'));
      
      if (values.weighingDates && Array.isArray(values.weighingDates)) {
        values.weighingDates.forEach((date: dayjs.Dayjs, index: number) => {
          formData.append(`weighingDates[${index}]`, date.format('YYYY-MM-DD'));
        });
      }
      
      formData.append('feeForClubMembers', String(values.feeForClubMembers || 0));
      formData.append('feeForOthers', String(values.feeForOthers || 0));
      formData.append('status', values.status);
      formData.append('autoRegistration', String(values.autoRegistration || false));
      formData.append('isInviteOnly', String(values.isInviteOnly || false));
      formData.append('hasTwoThirdPlaces', String(values.hasTwoThirdPlaces || false));
      formData.append('disciplineType', values.disciplineType || '');
      if (values.disciplineType === 'kumite' && values.victoryByPoints != null) {
        formData.append('victoryByPoints', String(values.victoryByPoints));
      } else {
        formData.append('victoryByPoints', '');
      }
      
      if (values.categoryIds && Array.isArray(values.categoryIds)) {
        values.categoryIds.forEach((categoryId: number, index: number) => {
          formData.append(`categoryIds[${index}]`, String(categoryId));
        });
      }

      // Добавляем приглашенные клубы, если турнир по приглашениям
      if (values.isInviteOnly && values.invitedClubIds && Array.isArray(values.invitedClubIds)) {
        values.invitedClubIds.forEach((clubId: number, index: number) => {
          formData.append(`invitedClubIds[${index}]`, String(clubId));
        });
      }

      // ВАЖНО: Отправляем обложку как первый файл в массиве files
      // Backend определяет первое изображение как coverImage
      // Обложка должна быть первой, затем документы
      if (coverImageFileList.length > 0 && coverImageFileList[0].originFileObj) {
        formData.append('files', coverImageFileList[0].originFileObj);
      }

      // Добавляем документы после обложки (если она есть)
      documentFileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      // Отправляем FormData через PUT запрос
      await apiService.put(`${API_ENDPOINTS.TOURNAMENTS.BASE}/${tournamentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Обновляем связи с категориями
      if (values.categoryIds?.length) {
        const categoryData = values.categoryIds.map((categoryId: number) => ({
          tournamentId: tournamentId,
          categoryId: categoryId,
          status: 'active'
        }));

        await TournamentService.updateCategories(tournamentId, categoryData);
      }

      message.success('Турнир успешно обновлен');
      form.resetFields();
      setDocumentFileList([]);
      setCoverImageFileList([]);
      onSuccess();
      // Родитель обновит контент через onSuccess, остаёмся на странице
    } catch (error) {
      logger.error('Error updating tournament:', error);
      message.error('Ошибка при обновлении турнира');
    } finally {
      setSubmitting(false);
    }
  };

  const beforeUpload = (file: File) => {
    // Проверяем, что это не изображение (документы только)
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      message.error('Для загрузки изображений используйте поле "Фото турнира". Здесь можно загружать только документы!');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Файл должен быть меньше 10MB!');
      return false;
    }
    return false; // Предотвращаем автоматическую загрузку
  };

  const beforeUploadCoverImage = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Можно загружать только изображения!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Изображение должно быть меньше 5MB!');
      return false;
    }
    return false; // Предотвращаем автоматическую загрузку
  };

  const handleDocumentChange = (info: any) => {
    setDocumentFileList(info.fileList);
  };

  const handleCoverImageChange = (info: any) => {
    setCoverImageFileList(info.fileList.slice(-1)); // Разрешаем только одно изображение
  };

  return (
    <Modal
      title="Редактирование турнира"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Сохранить"
      cancelText="Отмена"
      width={800}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* Поле для загрузки фото - в самом верху */}
        <Form.Item
          name="coverImage"
          label="Фото турнира"
          extra="Рекомендуемый размер: не более 5MB, форматы: JPEG, PNG"
        >
          <Upload
            listType="picture-card"
            fileList={coverImageFileList}
            onChange={handleCoverImageChange}
            beforeUpload={beforeUploadCoverImage}
            accept="image/*"
            maxCount={1}
            onRemove={() => setCoverImageFileList([])}
          >
            {coverImageFileList.length < 1 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Загрузить</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: 'Введите название турнира' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="location"
          label="Место проведения"
          rules={[{ required: true, message: 'Укажите место проведения' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="tournamentDate"
          label="Дата проведения"
          rules={[{ required: true, message: 'Выберите дату проведения' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату (ДД.ММ.ГГГГ)"
          />
        </Form.Item>

        <Form.Item
          name="registrationDeadline"
          label="Окончание регистрации"
          rules={[{ required: true, message: 'Укажите окончание регистрации' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату (ДД.ММ.ГГГГ)"
          />
        </Form.Item>

        <Form.Item
          name="weighingDates"
          label="Даты взвешивания"
        >
          <DatePicker.RangePicker 
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            locale={customLocale}
            placeholder={['Начало', 'Конец']}
            disabledDate={() => false}
          />
        </Form.Item>

        <Form.Item
          name="feeForClubMembers"
          label="Взнос для членов клуба"
          initialValue={0}
        >
          <InputNumber<number>
            style={{ width: '100%' }}
            min={0}
            precision={2}
            formatter={(value) => `${value} ₽`}
            parser={(value) => {
              const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
              return parsed ? parseFloat(parsed) : 0;
            }}
          />
        </Form.Item>

        <Form.Item
          name="feeForOthers"
          label="Взнос для остальных участников"
          initialValue={0}
        >
          <InputNumber<number>
            style={{ width: '100%' }}
            min={0}
            precision={2}
            formatter={(value) => `${value} ₽`}
            parser={(value) => {
              const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
              return parsed ? parseFloat(parsed) : 0;
            }}
          />
        </Form.Item>

        <Form.Item
          name="categoryIds"
          label="Категории"
        >
          {loadingCategories ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка категорий...</div>
          ) : (
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '16px',
              backgroundColor: '#fafafa'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Row gutter={8} align="middle">
                  <Col flex="auto">
                    <Input
                      placeholder="Поиск по названию, возрасту или весу..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      allowClear
                      style={{ marginBottom: '8px' }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => {
                        const allIds = categories.map(c => c.id);
                        setSelectedCategories(allIds);
                        form.setFieldValue('categoryIds', allIds);
                      }}
                    >
                      Все
                    </Button>
                  </Col>
                  <Col>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => {
                        setSelectedCategories([]);
                        form.setFieldValue('categoryIds', []);
                      }}
                    >
                      Снять
                    </Button>
                  </Col>
                </Row>
                
                {selectedCategories.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
                      Выбрано: {selectedCategories.length}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedCategories.map(catId => {
                        const category = categories.find(c => c.id === catId);
                        if (!category) return null;
                        return (
                          <Tag
                            key={catId}
                            closable
                            onClose={() => {
                              const newSelected = selectedCategories.filter(id => id !== catId);
                              setSelectedCategories(newSelected);
                              form.setFieldValue('categoryIds', newSelected);
                            }}
                            color="blue"
                            style={{ margin: 0 }}
                          >
                            {category.name}
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Space>

              <div style={{ maxHeight: '350px', overflowY: 'auto', marginTop: '12px' }}>
                {categoriesByGender.male.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      color: '#1890ff'
                    }}>
                      Мужчины ({categoriesByGender.male.length})
                    </div>
                    <Row gutter={[8, 8]}>
                      {categoriesByGender.male.map(category => {
                        const isSelected = selectedCategories.includes(category.id);
                        return (
                          <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
                            <div
                              onClick={() => {
                                const newSelected = isSelected
                                  ? selectedCategories.filter(id => id !== category.id)
                                  : [...selectedCategories, category.id];
                                setSelectedCategories(newSelected);
                                form.setFieldValue('categoryIds', newSelected);
                              }}
                              style={{
                                padding: '8px 12px',
                                border: `2px solid ${isSelected ? '#1890ff' : '#d9d9d9'}`,
                                borderRadius: '6px',
                                backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#40a9ff';
                                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#d9d9d9';
                                  e.currentTarget.style.backgroundColor = '#fff';
                                }
                              }}
                            >
                              <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                {category.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                {category.ageFrom}-{category.ageTo} лет
                              </div>
                              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                {category.weightFrom}-{category.weightTo} кг
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                )}

                {categoriesByGender.female.length > 0 && (
                  <div>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      marginTop: '16px',
                      color: '#eb2f96'
                    }}>
                      Женщины ({categoriesByGender.female.length})
                    </div>
                    <Row gutter={[8, 8]}>
                      {categoriesByGender.female.map(category => {
                        const isSelected = selectedCategories.includes(category.id);
                        return (
                          <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
                            <div
                              onClick={() => {
                                const newSelected = isSelected
                                  ? selectedCategories.filter(id => id !== category.id)
                                  : [...selectedCategories, category.id];
                                setSelectedCategories(newSelected);
                                form.setFieldValue('categoryIds', newSelected);
                              }}
                              style={{
                                padding: '8px 12px',
                                border: `2px solid ${isSelected ? '#eb2f96' : '#d9d9d9'}`,
                                borderRadius: '6px',
                                backgroundColor: isSelected ? '#fff0f6' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#ff85c0';
                                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#d9d9d9';
                                  e.currentTarget.style.backgroundColor = '#fff';
                                }
                              }}
                            >
                              <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                {category.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                {category.ageFrom}-{category.ageTo} лет
                              </div>
                              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                {category.weightFrom}-{category.weightTo} кг
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                )}

                {categories.length === 0 && !loadingCategories && (
                  <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '20px' }}>
                    Нет доступных категорий
                  </div>
                )}

                {searchText && categoriesByGender.male.length === 0 && categoriesByGender.female.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '20px' }}>
                    Ничего не найдено
                  </div>
                )}
              </div>
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="autoRegistration"
          label="Автоматическая регистрация"
          valuePropName="checked"
          help="Если включено, регистрация участников подтверждается автоматически без участия организатора"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="isInviteOnly"
          label="Турнир по приглашениям"
          valuePropName="checked"
          help="Если включено, турнир будет виден только приглашенным клубам и организатору"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="hasTwoThirdPlaces"
          label="Два третьих места"
          valuePropName="checked"
          help="Если включено, количество боев = количество участников - 1 (два 3 места), иначе количество боев = количество участников"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="disciplineType"
          label="Дисциплина"
          help="Кумите — поединки по очкам, Ката — выполнение формальных комплексов"
        >
          <Select placeholder="Выберите тип дисциплины" allowClear>
            <Select.Option value="kumite">Кумите</Select.Option>
            <Select.Option value="kata">Ката</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.disciplineType !== currentValues.disciplineType}
        >
          {({ getFieldValue }) =>
            getFieldValue('disciplineType') === 'kumite' ? (
              <Form.Item
                name="victoryByPoints"
                label="Победа по очкам"
                help="Количество очков для досрочной победы в поединке (например, 8)"
                rules={[
                  {
                    required: true,
                    message: 'Укажите количество очков для победы'
                  }
                ]}
              >
                <InputNumber min={1} max={15} style={{ width: '100%' }} placeholder="Например, 8" />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.isInviteOnly !== currentValues.isInviteOnly}
        >
          {({ getFieldValue }) =>
            getFieldValue('isInviteOnly') ? (
              <Form.Item
                name="invitedClubIds"
                label="Пригласить клубы"
                rules={[
                  {
                    required: true,
                    message: 'Выберите хотя бы один клуб для приглашения'
                  }
                ]}
                help="Выберите клубы, которые будут приглашены на турнир"
              >
                <Select
                  mode="multiple"
                  placeholder="Выберите клубы"
                  loading={loadingClubs}
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  showSearch
                  filterOption={(input, option) => {
                    const label = typeof option?.children === 'string' 
                      ? option.children 
                      : String(option?.children || '');
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {clubs.map(club => (
                    <Select.Option key={club.id} value={club.id}>
                      {club.name} {club.city ? `(${club.city})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          initialValue="draft"
        >
          <Select placeholder="Выберите статус турнира">
            {Object.entries(TOURNAMENT_STATUS)
              .filter(([key]) => key !== 'DELETED')
              .map(([key, value]) => (
                <Select.Option key={key} value={value}>
                  {TOURNAMENT_STATUS_LABELS[value]}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="documents"
          label="Документы"
          extra="Можно загружать документы (PDF, DOC, DOCX и др.). Максимальный размер файла: 10MB"
        >
          <Upload
            fileList={documentFileList}
            onChange={handleDocumentChange}
            beforeUpload={beforeUpload}
            multiple
          >
            <div>
              <UploadOutlined /> Загрузить документы
            </div>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};